import { GameInformationTypeEnum, GameOption } from "entities/game/types";
import { MetadataTag } from "entities/metadataTags/types";
import { TagMatchingType, TagMatchRule } from "entities/modules/types";
import { ModuleTagType } from "entities/tagTypes/types";
import { Tag } from "entities/tags/types";
import { ThemeTagType } from "entities/themeTagTypes/types";
import { ThemeTag } from "entities/themeTags/types";
import groupBy from "lodash/groupBy";
import keyBy from "lodash/keyBy";
import upperFirst from "lodash/upperFirst";
import { v4 as uuidv4 } from "uuid";
import { TagBlock, TagOption } from "widgets/ModuleTagsWidget/type";

export const getAlgoliaTagsFilters = (data: TagBlock[]): string => {
  const filtersArray = data.reduce((acc: string[], item, index) => {
    const matchingType = item.matchingType;
    const isFirstItem = index === 0;
    const isNotEquals = matchingType === TagMatchingType.NOT_EQUALS;
    const prefix = isFirstItem ? "" : "AND ";

    // Metadata tags
    if (item.tagType === ModuleTagType.METADATA) {
      if (item.primaryTagId === GameInformationTypeEnum.GAME_FEATURES) {
        const featuresValues = item.selectedTags.map((tag) => tag.name);
        if (featuresValues.length) {
          if (isNotEquals) {
            // For NOT_EQUALS, split into separate NOT conditions
            const notConditions = featuresValues
              .map((value) => `${prefix}NOT metadata.features:"${value}"`)
              .join(" ");
            acc.push(notConditions);
          } else {
            // For AND, keep the grouping
            acc.push(
              `${prefix}(metadata.features:${featuresValues.map((value) => `"${value}"`).join(` OR metadata.features:`)})`,
            );
          }
        }
      }

      const selectedValues = item.selectedTags.map((tag) => tag.name);
      if (selectedValues.length) {
        if (isNotEquals) {
          // For NOT_EQUALS, split into separate NOT conditions
          const notConditions = selectedValues
            .map(
              (value) =>
                `${prefix}NOT metadata.gameInformation.value:"${value}"`,
            )
            .join(" ");
          acc.push(notConditions);
        } else {
          // For AND, keep the grouping
          acc.push(
            `${prefix}(metadata.gameInformation.value:${selectedValues.map((value) => `"${value}"`).join(" OR metadata.gameInformation.value:")})`,
          );
        }
      }
    }

    // Theme tags
    if (item.tagType === ModuleTagType.THEME) {
      const themeValues = item.selectedTags.map((tag) => tag.id);
      if (themeValues.length) {
        if (isNotEquals) {
          // For NOT_EQUALS, split into separate NOT conditions
          const notConditions = themeValues
            .map((id) => `${prefix}NOT themeTags:${id}`)
            .join(" ");
          acc.push(notConditions);
        } else {
          // For AND, keep the grouping
          acc.push(
            `${prefix}(${themeValues.map((id) => `themeTags:${id}`).join(" OR ")})`,
          );
        }
      }
    }

    // Service tags
    if (item.tagType === ModuleTagType.SERVICE) {
      const serviceValues = item.selectedTags.map((tag) => tag.id);
      if (serviceValues.length) {
        if (isNotEquals) {
          // For NOT_EQUALS, split into separate NOT conditions
          const notConditions = serviceValues
            .map((id) => `${prefix}NOT tags:${id}`)
            .join(" ");
          acc.push(notConditions);
        } else {
          // For AND, keep the grouping
          acc.push(
            `${prefix}(${serviceValues.map((id) => `tags:${id}`).join(" OR ")})`,
          );
        }
      }
    }

    return acc;
  }, []);

  // Combine each group's filter condition with the AND operator
  const filters = filtersArray.length
    ? [filtersArray.join(" "), `isActive:true`].join(" AND ")
    : "";

  return filters;
};

type FormatTagsWithTypesProps = {
  tagBlocks: TagBlock[];
  option: GameOption;
  themePrimaryTags?: ThemeTagType[];
  metadataTags?: MetadataTag[];
};

export const formatTagsWithTypes = ({
  tagBlocks,
  option,
  themePrimaryTags = [],
  metadataTags = [],
}: FormatTagsWithTypesProps): string => {
  if (!tagBlocks?.length) return "";

  const result: string[] = [];

  const { tagTypes, tags, themeTagTypes, themeTags: optionThemeTags } = option;
  const serviceTagMap =
    tagTypes?.length && tags?.length
      ? new Map(tags.map((tagId, index) => [tagId, tagTypes[index]]))
      : null;
  const allowedServicePrimary = tagTypes?.length ? new Set(tagTypes) : null;

  const allowedThemePrimary = themeTagTypes?.length
    ? new Set(themeTagTypes)
    : null;
  const allowedThemeTags = optionThemeTags?.length
    ? new Set(optionThemeTags)
    : null;
  const themePrimaryTagMap = themePrimaryTags.length
    ? new Map(themePrimaryTags.map((tag) => [tag.id, tag]))
    : null;

  const metadataTagMap = metadataTags.length
    ? new Map(metadataTags.map((tag) => [tag.id, tag]))
    : null;

  for (const tagBlock of tagBlocks) {
    if (!tagBlock.primaryTagId) continue;

    switch (tagBlock.tagType) {
      case ModuleTagType.SERVICE: {
        if (!serviceTagMap || !allowedServicePrimary) break;
        if (!allowedServicePrimary.has(tagBlock.primaryTagId)) break;
        const selected = tagBlock.selectedTags;
        if (!selected) break;

        for (const selectedTag of selected) {
          if (serviceTagMap.has(selectedTag.id)) {
            const tagType = serviceTagMap.get(selectedTag.id);
            if (tagType) {
              result.push(`${upperFirst(tagType)}: ${selectedTag.name}`);
            }
          }
        }
        break;
      }
      case ModuleTagType.THEME: {
        if (!allowedThemePrimary || !allowedThemeTags || !themePrimaryTagMap)
          break;
        if (!allowedThemePrimary.has(tagBlock.primaryTagId)) break;
        const themeTag = themePrimaryTagMap.get(tagBlock.primaryTagId);
        if (!themeTag) break;

        const selected = tagBlock.selectedTags;
        if (!selected) break;
        for (const selectedTag of selected) {
          if (allowedThemeTags.has(selectedTag.id)) {
            result.push(`${themeTag.name}: ${selectedTag.name}`);
          }
        }
        break;
      }
      case ModuleTagType.METADATA: {
        if (!metadataTagMap) break;
        const metadataTag = metadataTagMap.get(tagBlock.primaryTagId);
        if (!metadataTag) break;

        const selected = tagBlock.selectedTags;
        if (selected && selected.some((tag) => tag.name)) {
          for (const tag of selected) {
            if (tag.name) {
              result.push(`${metadataTag.primaryTag}: ${tag.name}`);
            }
          }
        } else {
          result.push(`${metadataTag.primaryTag}`);
        }
        break;
      }
      default:
        break;
    }
  }

  return result.join(" | ");
};

type SerializeModuleTagsToBlocksProps = {
  moduleTagTypes: string[];
  moduleTags: string[];
  tagsMatchRules: TagMatchRule[];
  tags: Tag[];
  metadataTags: MetadataTag[];
  themeTags: ThemeTag[];
};

type PrimaryTagId = string;
type SecondaryTagId = string;

export const serializeModuleTagsToBlocks = ({
  moduleTagTypes,
  moduleTags,
  tagsMatchRules,
  tags,
  metadataTags = [],
  themeTags = [],
}: SerializeModuleTagsToBlocksProps): TagBlock[] => {
  const metadataTagsMap: Record<PrimaryTagId, MetadataTag> = keyBy(
    metadataTags,
    "primaryTag",
  );
  const serviceTags: Record<PrimaryTagId, Tag[]> = groupBy(tags, "tagType");
  const tagsMap: Record<SecondaryTagId, Tag> = keyBy(tags, "id");
  const themeTagsMap: Record<SecondaryTagId, ThemeTag> = keyBy(themeTags, "id");
  const tagsMatchRulesMap: Record<PrimaryTagId, TagMatchRule> = keyBy(
    tagsMatchRules,
    "primaryTagId",
  );

  return moduleTagTypes.map((tagTypeId) => {
    const selectedTags: TagOption[] = [];
    const addedTagIds = new Set<string>();

    moduleTags.forEach((tagId) => {
      if (addedTagIds.has(tagId)) {
        return;
      }

      if (metadataTagsMap[tagTypeId]) {
        const metadataTag = metadataTagsMap[tagTypeId];

        metadataTag.secondaryTags?.forEach((secondaryTag: string) => {
          if (secondaryTag === tagId) {
            selectedTags.push({
              id: secondaryTag,
              name: secondaryTag,
            });
          }
        });

        addedTagIds.add(metadataTag.id);
        return;
      }

      const tagData = tagsMap[tagId];

      if (tagData && tagData.tagType === tagTypeId) {
        selectedTags.push({
          id: tagData.id,
          name: tagData.name,
        });
        addedTagIds.add(tagData.id);
        return;
      }

      const themeTag = themeTagsMap[tagId];

      if (themeTag && themeTag.tagType === tagTypeId) {
        selectedTags.push({
          id: themeTag.id,
          name: themeTag.name,
        });
        addedTagIds.add(themeTag.id);
        return;
      }
    });

    const getTagType = () => {
      if (metadataTagsMap[tagTypeId]) {
        return ModuleTagType.METADATA;
      }

      if (serviceTags[tagTypeId]) {
        return ModuleTagType.SERVICE;
      }

      return ModuleTagType.THEME;
    };

    const getMatchingType = () => {
      const tagsMatchRule = tagsMatchRulesMap[tagTypeId];

      if (tagsMatchRule?.matchType === TagMatchingType.NOT_EQUALS) {
        return TagMatchingType.NOT_EQUALS;
      }

      return TagMatchingType.EQUALS;
    };

    return {
      id: uuidv4(),
      tagType: getTagType(),
      primaryTagId: tagTypeId,
      selectedTags,
      matchingType: getMatchingType(),
    };
  });
};

export const getTagsMatchRules = (tagBlocks: TagBlock[]): TagMatchRule[] => {
  if (!tagBlocks?.length) {
    return [];
  }

  const hasValidPrimaryTagId = (
    block: TagBlock,
  ): block is TagBlock & { primaryTagId: string } => {
    return !!block.primaryTagId;
  };

  return tagBlocks.filter(hasValidPrimaryTagId).map((block) => ({
    primaryTagId: block.primaryTagId,
    matchType: block.matchingType ?? TagMatchingType.EQUALS,
  }));
};
